import React, { useEffect, useState } from "react";

import { css, keyframes } from "@compiled/react";
import Lottie from "react-lottie";
import sanitizeHtml from "sanitize-html";
import { AuxiaResponse } from "src/pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment";

import { media } from "@atlaskit/primitives/responsive";

import {
    Carousel,
    VideoCarousel,
    CarouselProps,
    VideoCarouselProps,
    VideoSlide
} from "@/components/confluence/carousel/src";
import {
    AnimatedJiraTour,
    AnimatedJiraTourProps
} from "@/components/product-demos/animated-jira-tour/src/animated-jira-tour";
import { Picture, PictureImageProps } from "@/components/wpl/image/src";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import {
    AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
    sendConversionEventAlgolia
} from "@/patterns/global-nav/src/auto-complete-helpers";
import { getLocalStorage } from "@/utils/localstorage";

import { ClientOnly } from "../../../utils";

import { PaidAccountCreation } from "./paid-account-creation";

const formColumn = css({
    display: "flex",
    flexDirection: "column",
    [media.above.md]: {
        maxWidth: "500px"
    },
    [media.below.md]: {
        alignItems: "center"
    }
});

const formColumnWithEyelash = css({
    [media.above.md]: {
        maxWidth: "497px"
    }
});
const HeaderEyelashStyle = css({
    position: "relative"
});

const contentStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: "32px",
    paddingBlockEnd: "5.5rem",
    justifyContent: "center",
    alignItems: "center",
    [media.only.md]: {
        paddingInlineStart: "60px",
        paddingInlineEnd: "60px"
    },
    [media.below.md]: {
        flexDirection: "column"
    },
    [media.below.sm]: {
        paddingInlineStart: "32px",
        paddingInlineEnd: "32px"
    }
});

const withAnimation = css({
    [media.below.md]: {
        flexDirection: "column-reverse"
    }
});

const signupFormContainer = css({
    maxWidth: "368px",
    width: "100%"
});

const headingBasicStyles = css({
    letterSpacing: 0,
    marginBlockStart: 0,
    marginBlockEnd: 0,
    fontWeight: 500,
    fontSize: "2rem",
    lineHeight: 1.25,
    textAlign: "center",
    [media.above.sm]: {
        textAlign: "left"
    },
    [media.only.lg]: {
        fontSize: "2.75rem",
        lineHeight: 1.18181818,
        marginBlockEnd: "16px"
    },
    [media.only.xl]: {
        fontSize: "3rem",
        lineHeight: "3.5rem",
        marginBlockEnd: "24px"
    }
});

const heavyFontWeightStyles = css({
    fontWeight: 800
});

const subheaderStyles = css({
    marginBlockEnd: "16px",
    textAlign: "center",
    [media.above.sm]: {
        textAlign: "left"
    }
});

const whiteFontColor = css({
    color: wacToken("color.neutral.light0")
});

const subheaderStylesWithEyelash = css({
    color: wacToken("color.primary.black"),
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    [media.below.md]: {
        display: "flex",
        justifyContent: "left"
    }
});

const imgContainer = css({
    width: "650px",
    alignSelf: "center",
    [media.below.md]: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column"
    },
    [media.below.sm]: {
        width: "auto"
    }
});

const staticImageContainer = css({
    display: "none",
    [media.below.md]: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column"
    }
});

const animationHeroContainer = css({
    position: "relative",
    aspectRatio: "800 / 550",
    height: "auto",
    minHeight: "1px",
    width: "100%",
    minWidth: "800px",
    maxWidth: "800px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [media.below.lg]: {
        minWidth: "600px",
        maxWidth: "800px"
    },
    [media.below.md]: {
        display: "none"
    },
    padding: "30px"
});

const carouselContainerStyles = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "700px",
    height: "600px",

    [media.below.sm]: {
        width: "100%",
        height: "100%"
    }
});

const squaresContainer = css({
    position: "absolute",
    width: "100%",
    height: "95%",
    display: "flex",
    flexDirection: "column"
});

const squareOne = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineEnd: 0
});

const squareTwo = css({
    position: "absolute",
    insetBlockEnd: 0,
    insetInlineStart: 0
});

const eyelashStyle = css({
    position: "absolute",
    display: "inline-block",
    insetBlockStart: "-25px",
    insetInlineStart: "-35px",
    width: "39px",
    height: "48px",
    [media.below.md]: {
        width: "31px",
        height: "39px"
    },
    [media.below.sm]: {
        width: "28px",
        height: "34px"
    }
});

const underlineStyles = css({
    height: "16px",
    width: "90px",
    marginBlockEnd: "16px",
    [media.below.md]: {
        alignItems: "left"
    },
    [media.below.sm]: {
        alignItems: "left"
    }
});

const strokeRight = keyframes({
    from: {
        clipPath: "polygon(0 0, 0 0, 0 100%, 0 100%)"
    },
    to: {
        clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
    }
});

const accentImgStyles = css({
    animation: `${strokeRight} ease-in-out .5s 1`,
    position: "relative"
});

const AnimatedJiraTourStyles = css({
    position: "absolute",
    insetBlockStart: "5%",
    insetInlineEnd: "10%",
    insetInlineStart: "10%",
    insetBlockEnd: "5%",
    zIndex: 10
});

const heroImageSubheaderStyles = css({
    padding: "0 10%"
});

const Squares = () => {
    return (
        <div css={squaresContainer}>
            <div css={squareOne}>
                <svg
                    width="311"
                    height="220"
                    viewBox="0 0 311 220"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M7.34559 1.71298L310.956 0.712733L305.579 218.935L0.637246 219.618L7.34559 1.71298Z"
                        fill="#FFA900"
                    />
                </svg>
            </div>
            <div css={squareTwo}>
                <svg
                    width="252"
                    height="219"
                    viewBox="0 0 252 219"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M2.76007 0.226074H251.614V212.007L0.0842285 218.146L2.76007 0.226074Z"
                        fill="#82B536"
                    />
                </svg>
            </div>
        </div>
    );
};

const EyeLash = () => {
    return (
        <Picture
            className="eyelash"
            image={{
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/f0scafs8p034p4uxb1r3f84m05g8s742.svg",
                alt: "",
                width: 99,
                height: 50
            }}
            css={[accentImgStyles, eyelashStyle]}
        />
    );
};

const Scribble = () => {
    return (
        <Picture
            className="scribble"
            image={{
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/4g12qeh147330006huh36yys682jpa05.svg",
                alt: "",
                width: 90,
                height: 16
            }}
            css={[accentImgStyles, underlineStyles]}
        />
    );
};

export interface Animation {
    lottieAnimationPromise?: Promise<unknown>;
    riveAnimation?: React.FunctionComponent;
}

export interface PaidHeroProps {
    readonly header: string;
    readonly image?: PictureImageProps;
    readonly subheader?: string;
    readonly darkSolidTheme?: boolean;
    readonly theme?: string;
    readonly showAnimatedHero?: boolean;
    readonly showStaticImage?: boolean;
    readonly carouselHeroProps?: CarouselProps | VideoCarouselProps;
    readonly animatedJiraTourProps?: AnimatedJiraTourProps;
    readonly useNewHero?: boolean;
    readonly heroImageSubheader?: string;
    readonly hasRebrandedSignupFormStyles?: boolean;
    readonly showScribble?: boolean;
    readonly showEyeLash?: boolean;
    readonly showKeywordAnimation?: boolean;
    readonly animation?: Animation;
    readonly hasBannerAlert?: boolean;
    readonly heroBgColor?: BgColors;
    readonly showFourSocialButtons?: boolean;
    readonly auxiaResponse?: AuxiaResponse | null;
}

const AnimationWrapper = ({
    lottieAnimationPromise,
    riveAnimation: RiveAnimation
}: Animation) => {
    const [lottieAnimationData, setLottieAnimationData] = useState<unknown>();
    useEffect(() => {
        (async () => {
            if (lottieAnimationPromise && !RiveAnimation) {
                const lottieAnimation = await lottieAnimationPromise;
                setLottieAnimationData(lottieAnimation);
            }
        })();
    }, [RiveAnimation, lottieAnimationPromise]);
    if (RiveAnimation) {
        return <RiveAnimation />;
    } else if (lottieAnimationData) {
        return (
            <Lottie
                options={{
                    loop: false,
                    autoplay: true,
                    animationData: lottieAnimationData,
                    rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice"
                    }
                }}
            />
        );
    }
    return (
        <svg
            data-testid="default-animation"
            viewBox="0 0 600 350"
            width="600"
            height="350"
            style={{ width: "100%" }}
        />
    );
};

// eslint-disable-next-line complexity
export const PaidHeroSectionTemplate = ({
    header,
    subheader,
    image,
    darkSolidTheme,
    showAnimatedHero,
    carouselHeroProps,
    animatedJiraTourProps,
    heroImageSubheader,
    hasRebrandedSignupFormStyles,
    showScribble,
    showEyeLash,
    animation,
    hasBannerAlert = false,
    heroBgColor = "bgWhite",
    showFourSocialButtons,
    auxiaResponse
}: PaidHeroProps) => {
    // the default behavior is to show the animated hero image based on darkSolidTheme
    // however we can override this behavior if showAnimatedHero is explicitly set
    const showAnimated =
        showAnimatedHero !== undefined ? showAnimatedHero : !darkSolidTheme;

    useEffect(() => {
        const getaaCompleteQueryObj = getLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
        if (getaaCompleteQueryObj) {
            const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
            sendConversionEventAlgolia({
                objectID: parsedStringifyAttributes.objectID,
                queryID: parsedStringifyAttributes.queryID,
                position: parsedStringifyAttributes.position
            });
        }
    }, []);

    return (
        <div
            css={[
                contentStyle,
                animation && withAnimation,
                !hasBannerAlert &&
                    css({
                        paddingBlockStart: darkSolidTheme ? "60px" : "40px"
                    })
            ]}
        >
            <div
                css={[
                    formColumn,
                    showEyeLash && formColumnWithEyelash,
                    heroBgColor !== "bgWhite" && whiteFontColor
                ]}
            >
                <div css={showEyeLash && HeaderEyelashStyle}>
                    {showEyeLash && <EyeLash />}
                    <h1
                        css={[
                            headingBasicStyles,
                            hasRebrandedSignupFormStyles &&
                                heavyFontWeightStyles
                        ]}
                    >
                        {header}
                    </h1>
                </div>
                {darkSolidTheme && (
                    <span
                        css={[
                            subheaderStyles,
                            heroBgColor !== "bgWhite" && whiteFontColor
                        ]}
                    >
                        {subheader}
                    </span>
                )}
                {showScribble && (
                    <>
                        <span css={subheaderStylesWithEyelash}>
                            {subheader}
                        </span>
                        <Scribble />
                    </>
                )}

                <div css={signupFormContainer}>
                    <ClientOnly>
                        <PaidAccountCreation
                            darkSolidTheme={darkSolidTheme}
                            showFourSocialButtons={showFourSocialButtons}
                            auxiaResponse={auxiaResponse}
                        />
                    </ClientOnly>
                </div>
            </div>
            {animation && (
                <div>
                    <AnimationWrapper {...animation} />
                </div>
            )}
            {showAnimated && (
                <div css={animationHeroContainer}>
                    {(darkSolidTheme || hasRebrandedSignupFormStyles) && (
                        <Squares />
                    )}
                    <AnimatedJiraTour
                        css={AnimatedJiraTourStyles}
                        {...animatedJiraTourProps}
                    />
                </div>
            )}
            {carouselHeroProps ? (
                (carouselHeroProps.slides?.[0] as VideoSlide)?.[
                    "videoSrcMp4"
                ] ? (
                    <div css={carouselContainerStyles}>
                        <VideoCarousel
                            {...(carouselHeroProps as VideoCarouselProps)}
                        />
                    </div>
                ) : (
                    <div css={carouselContainerStyles}>
                        <Carousel {...(carouselHeroProps as CarouselProps)} />
                    </div>
                )
            ) : (
                (image || heroImageSubheader) && (
                    <div
                        css={[
                            showAnimated ? staticImageContainer : imgContainer
                        ]}
                    >
                        {image && (
                            <Picture
                                image={image}
                                style={{ maxWidth: "100%", height: "auto" }}
                            />
                        )}
                        {heroImageSubheader && (
                            <div
                                css={[
                                    heroImageSubheaderStyles,
                                    heroBgColor !== "bgWhite" && whiteFontColor
                                ]}
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(heroImageSubheader)
                                }}
                            />
                        )}
                    </div>
                )
            )}
        </div>
    );
};

PaidHeroSectionTemplate.displayName = "PaidHero";
